import {
  DOC_KINDS,
  DOC_STATUSES,
  type DocKind,
  type DocStatus,
  ITEM_KINDS,
  ITEM_PROPS,
  type ItemProp,
  WAREHOUSE_KINDS,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus, QrCode } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, tenge } from '../../lib/format';
import { useCan, useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { Exchange1cTab } from './Exchange1cTab';
import { DocumentDialog, ItemForm, ReservationDialog, WarehouseForm } from './forms';
import { IncomingControlTab } from './IncomingControlTab';
import { PoaPanel } from './PoaPanel';
import { PpeTab } from './PpeTab';
import {
  DOC_TONE,
  documentsQuery,
  holdingsQuery,
  itemsQuery,
  qty,
  reservationsQuery,
  stockQuery,
  warehousesQuery,
} from './queries';
import { ReportsTab } from './ReportsTab';
import { SourcingDialog, type SourcingPreset } from './SourcingDialog';
import { StockTable } from './StockTable';
import { InventoriesTab, ToolsTab } from './tabs2';

type Tab =
  | 'stock'
  | 'documents'
  | 'warehouses'
  | 'items'
  | 'reservations'
  | 'holdings'
  | 'inventories'
  | 'tools'
  | 'reports'
  | 'ppe'
  | 'qc'
  | 'exchange';
const TABS: Array<{ key: Tab; title: string }> = [
  { key: 'stock', title: 'Остатки' },
  { key: 'documents', title: 'Документы' },
  { key: 'warehouses', title: 'Склады' },
  { key: 'items', title: 'Каталог ТМЦ' },
  { key: 'reservations', title: 'Резервы' },
  { key: 'holdings', title: 'На руках' },
  { key: 'inventories', title: 'Инвентаризация' },
  { key: 'tools', title: 'Инструмент' },
  { key: 'reports', title: 'Отчёты' },
  { key: 'ppe', title: 'СИЗ' },
  { key: 'qc', title: 'Входной контроль' },
  { key: 'exchange', title: '1С' },
];

export function WarehousePage() {
  const navigate = useNavigate();
  const params = useSearch({ strict: false }) as { tab?: string };
  const tab: Tab = (TABS.find((t) => t.key === params.tab)?.key ?? 'stock') as Tab;
  const pick = (t: Tab) => void navigate({ to: '/warehouse', search: t === 'stock' ? {} : { tab: t } });
  useCrumbs(tab === 'stock' ? [] : [{ label: TABS.find((x) => x.key === tab)!.title }]);
  return (
    <Page>
      <PageHeader
        title="Склад"
        subtitle="Один учёт на все склады: остаток считается только из проведённых документов, ошибка исправляется сторно, резерв и «доступно» — одной функцией (СК-04)"
      />
      <WarehouseInboxStrip />
      <div
        className="mb-4 flex flex-wrap gap-1 rounded-md border border-line bg-surface p-0.5"
        role="tablist"
        aria-label="Склад"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => pick(t.key)}
            className={clsx(
              'h-11 min-w-11 rounded-[5px] px-3 text-[13px] font-medium',
              tab === t.key ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
            )}
          >
            {t.title}
          </button>
        ))}
      </div>
      {tab === 'stock' ? <StockTab /> : null}
      {tab === 'documents' ? <DocumentsTab /> : null}
      {tab === 'warehouses' ? <WarehousesTab /> : null}
      {tab === 'items' ? <ItemsTab /> : null}
      {tab === 'reservations' ? <ReservationsTab /> : null}
      {tab === 'holdings' ? <HoldingsTab /> : null}
      {tab === 'inventories' ? <InventoriesTab /> : null}
      {tab === 'tools' ? <ToolsTab /> : null}
      {tab === 'reports' ? <ReportsTab /> : null}
      {tab === 'ppe' ? <PpeTab /> : null}
      {tab === 'qc' ? <IncomingControlTab /> : null}
      {tab === 'exchange' ? <Exchange1cTab /> : null}
    </Page>
  );
}

/** Дела склада, которые ждут человека: подтвердить выдачу, решить по расходу, вернуть или проверить инструмент */
function WarehouseInboxStrip() {
  const q = useQuery({
    queryKey: ['wh', 'inbox'],
    queryFn: () =>
      api<
        Array<{
          id: string;
          title: string;
          details: string | null;
          overdue: boolean;
          actionLabel: string;
          actionPath: string;
        }>
      >('/warehouse/inbox'),
    staleTime: 30_000,
  });
  const items = q.data ?? [];
  if (!items.length) return null;
  return (
    <Panel className="mb-4" title="Ждёт вас по складу" meta={`${items.length}`} bodyClassName="p-0">
      <ul className="divide-y divide-line">
        {items.slice(0, 8).map((i) => (
          <li
            key={i.id}
            className="flex flex-col gap-1 px-4 py-3 text-[13px] sm:flex-row sm:items-center sm:gap-4"
          >
            <span className="flex-1">
              <span className="font-medium text-ink">{i.title}</span>
              {i.details ? <span className="text-ink-2"> · {i.details}</span> : null}
            </span>
            <span className="flex items-center gap-2">
              {i.overdue ? <Badge tone="danger">просрочено</Badge> : null}
              <Link
                to={i.actionPath}
                className="inline-flex h-11 items-center rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline"
              >
                {i.actionLabel}
              </Link>
            </span>
          </li>
        ))}
        {items.length > 8 ? (
          <li className="px-4 py-2 text-[12px] text-ink-2">и ещё {items.length - 8}</li>
        ) : null}
      </ul>
    </Panel>
  );
}

/* ───────────────────────── Остатки ───────────────────────── */

function StockTab() {
  const navigate = useNavigate();
  const warehouses = useQuery(warehousesQuery);
  const [warehouseId, setWarehouseId] = useState('');
  const [q, setQ] = useState('');
  // Ссылка из заявки на закуп: /warehouse?find=…&unit=…&qty=…&project=…&line=… открывает подбор сразу
  const sp = useSearch({ strict: false }) as SourcingPreset & {
    find?: string;
    project?: string;
    qty?: string;
    line?: string;
  };
  const preset: SourcingPreset | undefined =
    sp.find || sp.line
      ? { name: sp.find, unit: sp.unit, quantity: sp.qty, projectId: sp.project, requestLineId: sp.line }
      : undefined;
  const [sourcing, setSourcing] = useState(Boolean(preset));
  const stock = useQuery(
    stockQuery({ warehouseId: warehouseId || undefined, q: q.trim().length >= 2 ? q.trim() : undefined }),
  );
  const rows = stock.data ?? [];
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select
          className="h-9 w-auto max-w-full"
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
        >
          <option value="">Все склады</option>
          {(warehouses.data ?? []).map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
              {w.projectCode ? ` · ${w.projectCode}` : ''}
            </option>
          ))}
        </Select>
        <Input
          type="search"
          className="h-9 max-w-[320px]"
          placeholder="Позиция или код"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Button variant="secondary" onClick={() => setSourcing(true)}>
          Подобрать со склада
        </Button>
        {sourcing ? (
          <SourcingDialog
            open
            onOpenChange={(v) => {
              setSourcing(v);
              // Закрыли подбор из заявки — ссылка на строку из адреса убирается, кнопка открывает свободный поиск
              if (!v && preset) navigate({ to: '/warehouse', search: { tab: 'stock' } });
            }}
            preset={preset}
          />
        ) : null}
      </div>
      <Panel bodyClassName="p-0">
        {stock.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : stock.isError ? (
          <ErrorBox error={stock.error} className="m-5" />
        ) : rows.length ? (
          <StockTable rows={rows} />
        ) : (
          <div className="p-5">
            <EmptyState title="Остатков пока нет">
              Остаток появляется после проведения поступления или остатка на начало.
            </EmptyState>
          </div>
        )}
      </Panel>
    </>
  );
}

/* ───────────────────────── Документы ───────────────────────── */

function DocumentsTab() {
  const navigate = useNavigate();
  const warehouses = useQuery(warehousesQuery);
  const canOperate = (warehouses.data ?? []).some((w) => w.canOperate);
  const [kind, setKind] = useState<'' | DocKind>('');
  const [status, setStatus] = useState<'' | DocStatus>('');
  const [warehouseId, setWarehouseId] = useState('');
  const [creating, setCreating] = useState(false);
  const docs = useQuery(
    documentsQuery({
      kind: kind || undefined,
      status: status || undefined,
      warehouseId: warehouseId || undefined,
    }),
  );
  const rows = docs.data ?? [];
  const hasAmount = rows.some((r) => r.amount !== null);
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select
          className="h-9 w-auto max-w-full"
          value={kind}
          onChange={(e) => setKind(e.target.value as DocKind | '')}
        >
          <option value="">Все виды</option>
          {(Object.keys(DOC_KINDS) as DocKind[]).map((k) => (
            <option key={k} value={k}>
              {DOC_KINDS[k].title}
            </option>
          ))}
        </Select>
        <Select
          className="h-9 w-auto max-w-full"
          value={status}
          onChange={(e) => setStatus(e.target.value as DocStatus | '')}
        >
          <option value="">Любой статус</option>
          {(Object.keys(DOC_STATUSES) as DocStatus[]).map((k) => (
            <option key={k} value={k}>
              {DOC_STATUSES[k]}
            </option>
          ))}
        </Select>
        <Select
          className="h-9 w-auto max-w-full"
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
        >
          <option value="">Все склады</option>
          {(warehouses.data ?? []).map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </Select>
        {canOperate ? (
          <Button
            variant="primary"
            className="ml-auto"
            icon={<Plus className="size-4" />}
            onClick={() => setCreating(true)}
          >
            Новый документ
          </Button>
        ) : null}
      </div>
      <Panel bodyClassName="p-0">
        {docs.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : docs.isError ? (
          <ErrorBox error={docs.error} className="m-5" />
        ) : rows.length ? (
          <>
            <ul className="divide-y divide-line md:hidden">
              {rows.map((d) => (
                <li key={d.id} className="flex flex-col gap-1 px-4 py-3 text-[13px]">
                  <span className="flex flex-wrap items-center justify-between gap-2">
                    <Link
                      to={`/warehouse/documents/${d.id}`}
                      className="text-[14px] font-medium text-ink hover:underline"
                    >
                      {d.number}
                    </Link>
                    <Badge tone={DOC_TONE[d.status]}>{DOC_STATUSES[d.status]}</Badge>
                  </span>
                  <span className="text-ink-2">
                    {DOC_KINDS[d.kind].title} · {date(d.docDate)} · {d.warehouseName}
                    {d.toWarehouseName ? ` → ${d.toWarehouseName}` : ''}
                  </span>
                  {d.basis || d.recipientName || d.counterpartyName ? (
                    <span className="text-ink-2">
                      {[d.basis, d.recipientName, d.counterpartyName].filter(Boolean).join(' · ')}
                    </span>
                  ) : null}
                  <span className="num flex justify-between gap-3 text-ink-2">
                    <span>{d.lines} стр.</span>
                    {d.amount !== null ? <span className="text-ink">{tenge(d.amount)}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
            <ScrollX className="hidden md:block">
              <table className="w-full min-w-[900px] text-[14px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="py-2.5 pr-3 pl-5 font-medium">Документ</th>
                    <th className="py-2.5 pr-3 font-medium">Дата</th>
                    <th className="py-2.5 pr-3 font-medium">Склад</th>
                    <th className="py-2.5 pr-3 font-medium">Основание</th>
                    <th className="py-2.5 pr-3 text-right font-medium">Строк</th>
                    {hasAmount ? <th className="py-2.5 pr-3 text-right font-medium">Сумма</th> : null}
                    <th className="py-2.5 pr-5 font-medium">Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => (
                    <tr key={d.id} className="border-t border-line align-top">
                      <td className="py-2.5 pr-3 pl-5">
                        <Link
                          to={`/warehouse/documents/${d.id}`}
                          className="font-medium text-ink hover:underline"
                        >
                          {d.number}
                        </Link>
                        <div className="text-[12px] text-ink-2">{DOC_KINDS[d.kind].title}</div>
                      </td>
                      <td className="num py-2.5 pr-3">{date(d.docDate)}</td>
                      <td className="py-2.5 pr-3">
                        {d.warehouseName}
                        {d.toWarehouseName ? (
                          <span className="text-ink-2"> → {d.toWarehouseName}</span>
                        ) : null}
                        {d.recipientName ? (
                          <div className="text-[12px] text-ink-2">{d.recipientName}</div>
                        ) : null}
                        {d.counterpartyName ? (
                          <div className="text-[12px] text-ink-2">{d.counterpartyName}</div>
                        ) : null}
                      </td>
                      <td className="py-2.5 pr-3 text-[13px] text-ink-2">
                        {d.basis ?? '—'}
                        {d.reversalOfNumber ? <div>сторно {d.reversalOfNumber}</div> : null}
                        {d.reversedByNumber ? (
                          <div className="text-danger">сторнирован {d.reversedByNumber}</div>
                        ) : null}
                      </td>
                      <td className="num py-2.5 pr-3 text-right">{d.lines}</td>
                      {hasAmount ? (
                        <td className="num py-2.5 pr-3 text-right">
                          {d.amount === null ? '—' : tenge(d.amount)}
                        </td>
                      ) : null}
                      <td className="py-2.5 pr-5">
                        <Badge tone={DOC_TONE[d.status]}>{DOC_STATUSES[d.status]}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          </>
        ) : (
          <div className="p-5">
            <EmptyState title="Документов нет">
              Поступление, перемещение, выдача и списание создаются кнопкой «Новый документ».
            </EmptyState>
          </div>
        )}
      </Panel>
      <PoaPanel />
      {creating ? (
        <DocumentDialog
          open
          onOpenChange={(v) => !v && setCreating(false)}
          onSaved={(d) => navigate({ to: `/warehouse/documents/${d.id}` })}
        />
      ) : null}
    </>
  );
}

/* ───────────────────────── Склады ───────────────────────── */

function WarehousesTab() {
  const navigate = useNavigate();
  const canCreate = useCan('warehouse.operate');
  const q = useQuery(warehousesQuery);
  const [creating, setCreating] = useState(false);
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[14px] text-ink-2">
          Центральный, приобъектные, открытые площадки, инструментальные, «в пути» и у подрядчиков (СК-01)
        </p>
        {canCreate ? (
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
            Новый склад
          </Button>
        ) : null}
      </div>
      <Panel bodyClassName="p-0">
        {q.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : q.isError ? (
          <ErrorBox error={q.error} className="m-5" />
        ) : q.data.length ? (
          <ul className="divide-y divide-line">
            {q.data.map((w) => (
              <li key={w.id} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 px-5 py-3">
                <div className="min-w-0">
                  <Link
                    to={`/warehouse/warehouses/${w.id}`}
                    className="text-[15px] font-medium text-ink hover:underline"
                  >
                    {w.name}
                  </Link>
                  <div className="text-[13px] text-ink-2">
                    {WAREHOUSE_KINDS[w.kind]}
                    {w.projectCode ? ` · ${w.projectCode}` : ''}
                    {w.objectName ? ` · ${w.objectName}` : ''}
                    {w.counterpartyName ? ` · ${w.counterpartyName}` : ''}
                    {w.molName ? ` · МОЛ ${w.molName}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
                  <span className="num">{w.itemsInStock} поз.</span>
                  {!w.isActive ? <Badge tone="neutral">закрыт</Badge> : null}
                  {w.canOperate ? <Badge tone="success">провожу</Badge> : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-5">
            <EmptyState title="Складов пока нет">Заведите центральный склад и склады объектов.</EmptyState>
          </div>
        )}
      </Panel>
      {creating ? (
        <Dialog open onOpenChange={(v) => !v && setCreating(false)} title="Новый склад" width="lg">
          <WarehouseForm
            onCancel={() => setCreating(false)}
            onSaved={(w) => navigate({ to: `/warehouse/warehouses/${w.id}` })}
          />
        </Dialog>
      ) : null}
    </>
  );
}

/* ───────────────────────── Каталог ───────────────────────── */

function ItemsTab() {
  const navigate = useNavigate();
  const canEdit = useCan('warehouse.operate');
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'' | 'material' | 'tool'>('');
  const [selected, setSelected] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const list = useQuery(
    itemsQuery({ q: q.trim().length >= 2 ? q.trim() : undefined, kind: kind || undefined }),
  );
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          type="search"
          className="h-9 max-w-[320px]"
          placeholder="Название, код или класс"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select className="h-9 w-auto" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          <option value="">Все</option>
          {(Object.keys(ITEM_KINDS) as Array<keyof typeof ITEM_KINDS>).map((k) => (
            <option key={k} value={k}>
              {ITEM_KINDS[k]}
            </option>
          ))}
        </Select>
        <div className="ml-auto flex flex-wrap gap-2">
          {selected.length ? (
            <Button
              icon={<QrCode className="size-4" />}
              onClick={() => navigate({ to: '/warehouse/labels', search: { items: selected.join(',') } })}
            >
              Этикетки: {selected.length}
            </Button>
          ) : null}
          {canEdit ? (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Новая позиция
            </Button>
          ) : null}
        </div>
      </div>
      <Panel bodyClassName="p-0">
        {list.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : list.isError ? (
          <ErrorBox error={list.error} className="m-5" />
        ) : list.data.length ? (
          <>
            <ul className="divide-y divide-line md:hidden">
              {list.data.map((it) => (
                <li key={it.id} className="flex items-start gap-2 py-2 pr-4 pl-2 text-[13px]">
                  <label className="flex size-11 shrink-0 cursor-pointer items-center justify-center">
                    <input
                      type="checkbox"
                      aria-label={`Выбрать ${it.code}`}
                      className="size-4"
                      checked={selected.includes(it.id)}
                      onChange={() => toggle(it.id)}
                    />
                  </label>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-1.5">
                    <Link
                      to={`/warehouse/items/${it.id}`}
                      className="text-[14px] font-medium text-ink hover:underline"
                    >
                      {it.name}
                    </Link>
                    <span className="num text-ink-2">
                      {it.code} · {it.unit}
                      {it.agskClass ? ` · ${it.agskClass}` : ''}
                    </span>
                    <span className="num flex justify-between gap-3 text-ink-2">
                      <span>остаток {qty(it.onHand)}</span>
                      <span className="font-semibold text-ink">доступно {qty(it.available)}</span>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            <ScrollX className="hidden md:block">
              <table className="w-full min-w-[760px] text-[14px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="w-10 py-2.5 pl-4">
                      <span className="sr-only">Выбрать</span>
                    </th>
                    <th className="py-2.5 pr-3 font-medium">Позиция</th>
                    <th className="py-2.5 pr-3 font-medium">Класс и свойства</th>
                    <th className="py-2.5 pr-3 font-medium">Ед.</th>
                    <th className="py-2.5 pr-3 text-right font-medium">Остаток</th>
                    <th className="py-2.5 pr-5 text-right font-medium">Доступно</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((it) => (
                    <tr key={it.id} className="border-t border-line align-top">
                      <td className="py-2.5 pl-4">
                        <input
                          type="checkbox"
                          aria-label={`Выбрать ${it.code}`}
                          className="size-4"
                          checked={selected.includes(it.id)}
                          onChange={() => toggle(it.id)}
                        />
                      </td>
                      <td className="py-2.5 pr-3">
                        <Link
                          to={`/warehouse/items/${it.id}`}
                          className="font-medium text-ink hover:underline"
                        >
                          {it.name}
                        </Link>
                        <div className="num text-[12px] text-ink-2">
                          {it.code}
                          {it.kind === 'tool' ? ' · инструмент' : ''}
                          {it.certRequired ? ' · сертификат' : ''}
                          {it.passportRequired ? ' · паспорт' : ''}
                        </div>
                      </td>
                      <td className="py-2.5 pr-3 text-[13px] text-ink-2">
                        {it.agskClass ? <span className="num">{it.agskClass} </span> : null}
                        {Object.entries(it.props)
                          .map(([k, v]) => `${ITEM_PROPS[k as ItemProp] ?? k} ${v}`)
                          .join(', ')}
                      </td>
                      <td className="py-2.5 pr-3">
                        {it.unit}
                        {it.convUnit ? (
                          <div className="num text-[12px] text-ink-2">
                            1 {it.unit} = {qty(it.convFactor)} {it.convUnit}
                          </div>
                        ) : null}
                      </td>
                      <td className="num py-2.5 pr-3 text-right">{qty(it.onHand)}</td>
                      <td className="num py-2.5 pr-5 text-right font-semibold">{qty(it.available)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          </>
        ) : (
          <div className="p-5">
            <EmptyState title="В каталоге пусто">
              Каталог один на все проекты: код, класс АГСК-3, свойства, единица и пересчёт (СК-02).
            </EmptyState>
          </div>
        )}
      </Panel>
      {creating ? (
        <Dialog open onOpenChange={(v) => !v && setCreating(false)} title="Новая позиция каталога" width="lg">
          <ItemForm
            onCancel={() => setCreating(false)}
            onSaved={(i) => navigate({ to: `/warehouse/items/${i.id}` })}
          />
        </Dialog>
      ) : null}
    </>
  );
}

/* ───────────────────────── Резервы ───────────────────────── */

function ReservationsTab() {
  const qc = useQueryClient();
  const warehouses = useQuery(warehousesQuery);
  const operable = new Set((warehouses.data ?? []).filter((w) => w.canOperate).map((w) => w.id));
  const canOperate = operable.size > 0;
  const [all, setAll] = useState(false);
  const [creating, setCreating] = useState(false);
  const q = useQuery(reservationsQuery({ all }));
  const release = useMutation({
    mutationFn: (id: string) => api(`/warehouse/reservations/${id}/release`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast('Резерв снят');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  // Запрос на взятие под другой проект решает владелец склада (СК-15): отказ — с причиной
  const decide = useMutation({
    mutationFn: (p: { id: string; approve: boolean; note?: string }) =>
      api<{ documentNumber: string | null }>(`/warehouse/reservations/${p.id}/decide`, {
        body: { approve: p.approve, note: p.note },
      }),
    onSuccess: (d, p) => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast(
        p.approve
          ? d.documentNumber
            ? `Разрешено, черновик перемещения ${d.documentNumber} создан`
            : 'Разрешено, резерв поставлен'
          : 'Отказано',
      );
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const refuse = (id: string) => {
    const note = window.prompt('Причина отказа');
    if (note?.trim()) decide.mutate({ id, approve: false, note: note.trim() });
  };
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select
          className="h-9 w-auto"
          value={all ? 'all' : 'active'}
          onChange={(e) => setAll(e.target.value === 'all')}
        >
          <option value="active">Действующие</option>
          <option value="all">Все, включая закрытые</option>
        </Select>
        {canOperate ? (
          <Button
            variant="primary"
            className="ml-auto"
            icon={<Plus className="size-4" />}
            onClick={() => setCreating(true)}
          >
            Поставить резерв
          </Button>
        ) : null}
      </div>
      <Panel bodyClassName="p-0">
        {q.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : q.isError ? (
          <ErrorBox error={q.error} className="m-5" />
        ) : q.data.length ? (
          <ul className="divide-y divide-line">
            {q.data.map((r) => (
              <li key={r.id} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-5 py-3">
                <div className="min-w-0">
                  <Link
                    to={`/warehouse/items/${r.itemId}`}
                    className="text-[15px] font-medium text-ink hover:underline"
                  >
                    {r.itemName}
                  </Link>
                  <span className="num ml-2 text-[14px]">
                    {qty(r.quantity)} {r.unit}
                  </span>
                  <div className="text-[13px] text-ink-2">
                    {r.warehouseName}
                    {r.workRef ? ` · работа ${r.workRef}` : ''}
                    {r.reason ? ` · ${r.reason}` : ''}
                  </div>
                  <div className="text-[12px] text-ink-2">
                    {r.createdByName ?? '—'}, {dateTime(r.createdAt)}
                    {r.decidedAt
                      ? ` · решение: ${r.decidedByName ?? '—'}, ${dateTime(r.decidedAt)}${r.decisionNote ? ` — ${r.decisionNote}` : ''}`
                      : ''}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    tone={
                      r.status === 'active'
                        ? 'info'
                        : r.status === 'requested'
                          ? 'warning'
                          : r.status === 'declined'
                            ? 'danger'
                            : 'neutral'
                    }
                  >
                    {r.status === 'active'
                      ? 'действует'
                      : r.status === 'requested'
                        ? 'ждёт согласия владельца склада'
                        : r.status === 'declined'
                          ? 'отказано'
                          : r.status === 'consumed'
                            ? 'закрыт выдачей'
                            : 'снят'}
                  </Badge>
                  {r.status === 'requested' && r.canDecide ? (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        loading={decide.isPending && decide.variables?.id === r.id}
                        onClick={() => decide.mutate({ id: r.id, approve: true })}
                      >
                        Разрешить
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => refuse(r.id)}>
                        Отказать
                      </Button>
                    </>
                  ) : null}
                  {r.status === 'requested' && !r.canDecide && r.mine ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={release.isPending && release.variables === r.id}
                      onClick={() => release.mutate(r.id)}
                    >
                      Отозвать
                    </Button>
                  ) : null}
                  {r.status === 'active' && operable.has(r.warehouseId) ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={release.isPending && release.variables === r.id}
                      onClick={() => release.mutate(r.id)}
                    >
                      Снять
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-5">
            <EmptyState title="Резервов нет">
              Резерв под работу уменьшает «доступно» и закрывается выдачей на работу (СК-11).
            </EmptyState>
          </div>
        )}
      </Panel>
      {creating ? <ReservationDialog open onOpenChange={(v) => !v && setCreating(false)} /> : null}
    </>
  );
}

/* ───────────────────────── На руках ───────────────────────── */

function HoldingsTab() {
  const me = useMe();
  const navigate = useNavigate();
  const q = useQuery(holdingsQuery());
  const [claiming, setClaiming] = useState(false);
  const mine = (q.data ?? []).filter((h) => h.holderUserId === me.user.id);
  const pendingMine = mine.filter((h) => h.pending > 0);
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[14px] text-ink-2">
          Выданное числится за сотрудником после его подтверждения; израсходованное, потерю или недостачу он
          заявляет сам, решение — за руководителем проекта (СК-21, СК-23)
        </p>
        {mine.some((h) => h.quantity > 0) ? (
          <Button variant="primary" onClick={() => setClaiming(true)}>
            Заявить расход или потерю
          </Button>
        ) : null}
      </div>
      {pendingMine.length ? (
        <div className="mb-4 rounded-md bg-info-soft px-4 py-3 text-[13px]">
          Вам выдано и ждёт подтверждения:{' '}
          {pendingMine.map((h) => `${h.itemName} — ${qty(h.pending)} ${h.unit}`).join('; ')}. Подтвердите
          получение в документе выдачи (вкладка «Документы», ваши выдачи).
        </div>
      ) : null}
      <Panel
        title="Что числится за сотрудниками"
        meta="подтверждённое получение минус возвращено и списано"
        bodyClassName="p-0"
      >
        {q.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : q.isError ? (
          <ErrorBox error={q.error} className="m-5" />
        ) : q.data.length ? (
          <>
            <ul className="divide-y divide-line md:hidden">
              {q.data.map((h) => (
                <li
                  key={`${h.holderUserId}:${h.itemId}`}
                  className="flex flex-col gap-0.5 px-4 py-3 text-[13px]"
                >
                  <span className="text-[14px] font-medium text-ink">{h.holderName}</span>
                  <Link to={`/warehouse/items/${h.itemId}`} className="hover:underline">
                    {h.itemName}
                  </Link>
                  <span className="num flex justify-between gap-3 text-ink-2">
                    <span>
                      {qty(h.quantity)} {h.unit}
                      {h.pending ? ` · ждёт подтверждения ${qty(h.pending)}` : ''}
                    </span>
                    <span>с {date(h.since)}</span>
                  </span>
                </li>
              ))}
            </ul>
            <ScrollX className="hidden md:block">
              <table className="w-full min-w-[640px] text-[14px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="py-2.5 pr-3 pl-5 font-medium">Сотрудник</th>
                    <th className="py-2.5 pr-3 font-medium">Позиция</th>
                    <th className="py-2.5 pr-3 text-right font-medium">Количество</th>
                    <th className="py-2.5 pr-5 font-medium">С какого числа</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.map((h) => (
                    <tr key={`${h.holderUserId}:${h.itemId}`} className="border-t border-line">
                      <td className="py-2.5 pr-3 pl-5 font-medium">{h.holderName}</td>
                      <td className="py-2.5 pr-3">
                        <Link to={`/warehouse/items/${h.itemId}`} className="hover:underline">
                          {h.itemName}
                        </Link>
                        <span className="num ml-2 text-[12px] text-ink-2">{h.itemCode}</span>
                      </td>
                      <td className="num py-2.5 pr-3 text-right">
                        {qty(h.quantity)} {h.unit}
                      </td>
                      <td className="num py-2.5 pr-5">{date(h.since)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          </>
        ) : (
          <div className="p-5">
            <EmptyState title="На руках ничего нет">
              Выдача на работу переводит материалы и инструмент на получателя до возврата или списания.
            </EmptyState>
          </div>
        )}
      </Panel>
      {claiming ? (
        <DocumentDialog
          open
          presetKind="consume"
          onOpenChange={(v) => !v && setClaiming(false)}
          onSaved={(d) => navigate({ to: `/warehouse/documents/${d.id}` })}
        />
      ) : null}
    </>
  );
}
